import { createErrorFromResponse, NoNetworkError, EmptyResponseBodyError } from "./error";
import type { JsonObject } from "type-fest";
import { sanitizeIds } from "./sanitizeIds";
import { dojoFetch } from "@web-monorepo/dojo-fetch";
import { isPrecanaryRequested } from "./precanary";

declare global {
  // eslint-disable-next-line no-var
  var appVersion: string | undefined;
  // eslint-disable-next-line no-var
  var useCanary: boolean | undefined;
  // eslint-disable-next-line no-var
  var usePrecanary: boolean | undefined;
  // eslint-disable-next-line no-var
  var usePreCanary: boolean | undefined;
  // eslint-disable-next-line no-var
  var useEKS: boolean | undefined;
  // eslint-disable-next-line no-var
  var usePerformance: boolean | undefined;
  // eslint-disable-next-line no-var
  var useStaging: boolean | undefined;
  // eslint-disable-next-line no-var
  var useClassToken: boolean | undefined;
}

// Copied from @web-monorepo/infra/callApi
export type Method = "GET" | "PUT" | "POST" | "PATCH" | "DELETE" | "HEAD";

export interface CallApiParams {
  method?: Method;
  /**
   * Request path. Usually relative and resolved against the app origin. An absolute URL is allowed
   * (an S3 presigned POST, for example) but is still decorated with the ClassDojo client headers,
   * environment query params and `credentials: "include"` unless `external` is also set - which
   * leaks credentials to the third party and forces a CORS preflight it will likely reject. Pass
   * `external: true` alongside any non-ClassDojo absolute URL.
   */
  path: string;
  query?: Record<string, string | boolean>;
  body?: Record<string, unknown> | Record<string, unknown>[] | BodyInit;
  headers?: Record<string, string>;
  route?: string;
  /**
   * Credentials mode for the fetch request.
   * Defaults to 'include' (unlike native fetch which defaults to 'same-origin').
   * This means credentials are sent cross-origin if CORS allows.
   * Defaults to 'omit' when `external` is set.
   */
  credentials?: "omit" | "same-origin" | "include";
  /**
   * Extra attempts after the first, for network failures and retryable statuses
   * (408, 413, 429, 500, 502, 503, 504, 521, 522, 524). Any other status fails on the
   * first attempt. Defaults to 0 - no retry.
   *
   * This mirrors superagent's `.retry(n)` predicate so call sites migrating off superagent
   * keep their retry behaviour. Retries are immediate; there is no backoff.
   *
   * Negative values are clamped to 0. The first attempt is unconditional either way, so a
   * negative count behaves exactly like 0 - one attempt, no retries.
   */
  retries?: number;
  /**
   * The request targets a third-party origin (e.g. an S3 presigned POST) rather than the
   * ClassDojo API. Skips the ClassDojo request decoration - client-identifier headers and the
   * environment query params - because those turn a simple cross-origin request into one
   * needing a CORS preflight the third party generally does not allow, and defaults
   * `credentials` to `omit`.
   */
  external?: boolean;
  /**
   * How to read a successful response body. Defaults to `"text"`, which parses JSON by content type.
   * Pass `"blob"` for binary payloads such as audio, because decoding them as text corrupts the bytes.
   * Error responses are always read as text, so their JSON error body is parsed as usual.
   */
  responseType?: "text" | "blob";
}
// End copied types

// This is a simplified Response type that matches what the fetchers seem to need from superagent's Response
export interface FetchResponse {
  body?: JsonObject;
  text: string;
  status: number;
  headers: Record<string, string>;
  request: CallApiParams;

  /**
   * @deprecated Use `request` instead. This property is maintained for compatibility with legacy code and will be removed in future releases.
   */
  req: CallApiParams;
  /**
   * @deprecated Use `status` instead. This property is maintained for compatibility with legacy code and will be removed in future releases.
   */
  statusCode: number;
}

export interface ApiSuccess<T> {
  success: true;
  data: T;
  text: string;
  status: number;
  headers: Record<string, string>;
}
export interface ApiError {
  success: false;
  error: Error;
  status: number;
}
export type ApiResponse<T> = ApiSuccess<T> | ApiError;

function jsClientHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "x-client-identifier": "Web",
    "x-sign-attachment-urls": "true",
  };
  if (global.appVersion) {
    headers["x-client-version"] = global.appVersion;
  }
  return headers;
}

function addEnvironmentQueryParams(query: Record<string, string | boolean>): void {
  if (global.useCanary) {
    query.useCanary = "true";
  } else if (isPrecanaryRequested()) {
    query.usePrecanary = "true";
  } else if (global.useEKS) {
    query.useEKS = "true";
  } else if (global.usePerformance) {
    query.usePerformance = "true";
  } else if (global.useStaging) {
    query.useStaging = "true";
  }

  if (global.useClassToken) {
    query.useClassToken = "true";
  }
}

function isRawBody(body: unknown): body is BodyInit {
  return typeof body === "string" || body instanceof Blob || body instanceof ArrayBuffer || body instanceof Uint8Array;
}

function findContentTypeHeader(headers: Record<string, string>): string | undefined {
  return Object.keys(headers).find((key) => key.toLowerCase() === "content-type");
}

function serializeRequestBody(
  body: CallApiParams["body"],
  headers: Record<string, string>,
  fetchOptions: RequestInit,
): void {
  if (!body) return;

  // multipart/form-data: the platform generates the `boundary` that belongs in the Content-Type, so
  // the header has to be left unset. A caller-supplied one loses the boundary and the server can't
  // parse the body.
  if (body instanceof FormData) {
    if (findContentTypeHeader(headers)) {
      throw new Error(
        "Cannot pass a FormData body with a Content-Type header. The multipart boundary is generated per request, so the header must be left to the platform.",
      );
    }
    fetchOptions.body = body;
    return;
  }

  if (isRawBody(body)) {
    fetchOptions.body = body;
    return;
  }

  // JSON-serialize objects/arrays
  // Throw if Content-Type is already set, as it conflicts with JSON serialization
  if (findContentTypeHeader(headers)) {
    throw new Error(
      "Cannot pass a JSON object/array body with a Content-Type header. Use a string/Uint8Array/Blob body instead.",
    );
  }
  fetchOptions.body = JSON.stringify(body);
  headers["Content-Type"] = "application/json";
}

function parseResponseBody(text: string, contentType: string | null): { body: unknown; error?: Error } {
  if (contentType?.includes("application/json")) {
    try {
      return { body: JSON.parse(text) };
    } catch (e) {
      if (e instanceof SyntaxError) {
        const error = new Error(`Failed to parse JSON response. Text: ${text}`);
        error.cause = e;
        return { body: undefined, error };
      }
      throw e;
    }
  }
  // For non-JSON content types, or if content-type is missing, treat body as text.
  return { body: text };
}

function buildErrorApiResponse(
  response: Response,
  url: URL,
  method: Method,
  path: string,
  body: CallApiParams["body"],
  finalHeaders: Record<string, string>,
  responseBody: unknown,
  text: string,
  responseHeaders: Record<string, string>,
  sanitizedRoute: string,
): ApiError {
  const req = {
    method,
    url: url.toString(),
    path,
    body,
    headers: finalHeaders,
  };
  const compatibleResponse: FetchResponse = {
    body: responseBody as JsonObject,
    text,
    status: response.status,
    statusCode: response.status,
    headers: responseHeaders,
    req,
    request: req,
  };

  // `createErrorFromResponse` accepts `ApiResponseLike`, which our `FetchResponse` satisfies —
  // no cast needed (see @web-monorepo/utils/infra's responseHandlers).
  const error = createErrorFromResponse(compatibleResponse, sanitizedRoute);
  return { success: false, error, status: response.status };
}

function shouldReportEmptyBody(
  url: URL,
  status: number,
  text: string,
  responseHeaders: Record<string, string>,
): boolean {
  if (status !== 200 || text) return false;
  const { hostname, pathname } = url;
  const isClassdojoApi =
    (hostname.endsWith(".classdojo.com") || hostname.endsWith(".classdojo.test")) && pathname.startsWith("/api");
  return isClassdojoApi && responseHeaders["x-intentionally-empty"] !== "true";
}

/**
 * A wrapper around the global `fetch` API that provides a consistent, Superagent-like
 * response shape for use within the fetchers library. It also handles project-specific
 * concerns such as adding environment-specific query parameters (e.g., `useCanary`)
 * and client-identifying headers.
 *
 * This function is intended to be the primary method for making API calls when the "fetch"
 * provider is active, replacing the older `infra/callApi` (Superagent) implementation.
 *
 * Non-2xx responses are returned as `{ success: false }` rather than resolved as success, and
 * `retries` opts into superagent-compatible retrying (see {@link CallApiParams.retries}).
 *
 * @param {CallApiParams} params - The parameters for the API call, including method, path, query, body, and headers.
 * @returns {Promise<ApiResponse<T>>} A promise that resolves to a standardized API response object,
 * indicating either success with data or an error.
 */
export function apiFetch<T>(params: CallApiParams & { responseType?: "text" }): Promise<ApiResponse<T>>;
/** Same as above, but a successful body is returned as a `Blob` (see {@link CallApiParams.responseType}). */
export function apiFetch(params: CallApiParams & { responseType: "blob" }): Promise<ApiResponse<Blob>>;
export async function apiFetch<T>(params: CallApiParams): Promise<ApiResponse<T> | ApiResponse<Blob>> {
  // The first attempt is unconditional; Math.max only keeps a negative `retries` from producing a
  // nonsensical loop bound. A negative count therefore behaves exactly like 0.
  const attempts = Math.max(0, params.retries ?? 0) + 1;

  let result = await attemptApiFetch<T>(params);
  for (let attempt = 2; attempt <= attempts && isRetryableFailure(result); attempt++) {
    result = await attemptApiFetch<T>(params);
  }

  return result;
}

// superagent's default retry predicate: a network failure, or one of these statuses. Anything else
// (a 400 from a bad request, a 403 from an expired signature) is not worth retrying.
const RETRYABLE_STATUSES = new Set([408, 413, 429, 500, 502, 503, 504, 521, 522, 524]);

function isRetryableFailure(result: ApiResponse<unknown>): boolean {
  if (result.success) return false;
  // status 0 is the shape attemptApiFetch returns when the request never reached a server.
  return result.status === 0 || RETRYABLE_STATUSES.has(result.status);
}

function buildFetchRequest({
  method = "GET",
  path,
  body,
  query = {},
  headers = {},
  credentials,
  external = false,
}: CallApiParams): { url: URL; finalHeaders: Record<string, string>; fetchOptions: RequestInit } {
  if (!external) {
    addEnvironmentQueryParams(query);
  }

  Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);

  const url = new URL(path, window.location.origin);
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  });

  const finalHeaders = external ? { ...headers } : { ...jsClientHeaders(), ...headers };

  const fetchOptions: RequestInit = {
    method,
    headers: finalHeaders,
    credentials: credentials ?? (external ? "omit" : "include"),
  };

  serializeRequestBody(body, finalHeaders, fetchOptions);

  return { url, finalHeaders, fetchOptions };
}

function emptyResponseBodyFailure({
  url,
  ...details
}: Omit<ConstructorParameters<typeof EmptyResponseBodyError>[0], "url"> & { url: URL }): ApiError {
  return {
    success: false,
    error: new EmptyResponseBodyError({ ...details, url: url.toString() }),
    status: details.status,
  };
}

async function attemptApiFetch<T>(params: CallApiParams): Promise<ApiResponse<T> | ApiSuccess<Blob>> {
  const { method = "GET", path, body, route } = params;
  const sanitizedRoute = route ? sanitizeIds(route) : sanitizeIds(path);
  const { url, finalHeaders, fetchOptions } = buildFetchRequest(params);

  let response: Response;
  try {
    response = await dojoFetch(url.toString(), { ...fetchOptions, path, route: sanitizedRoute });
  } catch (e) {
    // This catches actual network errors (e.g., offline, DNS failure)
    if (e instanceof Error) {
      return { success: false, error: e as NoNetworkError, status: 0 };
    }
    throw e;
  }

  const responseHeaders: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key.toLowerCase()] = value;
  });

  if (params.responseType === "blob" && response.ok) {
    const blob = await response.blob();
    if (blob.size === 0 && shouldReportEmptyBody(url, response.status, "", responseHeaders)) {
      return emptyResponseBodyFailure({
        path,
        method,
        url,
        headers: responseHeaders,
        status: response.status,
        route: sanitizedRoute,
      });
    }
    return { success: true, data: blob, text: "", status: response.status, headers: responseHeaders };
  }

  const text = await response.text();

  // A 204 No Content response will not have a body to parse.
  if (response.status === 204) {
    return { success: true, text: "", data: null as T, status: response.status, headers: responseHeaders };
  }

  const parsed = parseResponseBody(text, response.headers.get("content-type"));
  if (parsed.error) {
    return { success: false, error: parsed.error, status: response.status };
  }
  const responseBody = parsed.body;

  if (!response.ok) {
    return buildErrorApiResponse(
      response,
      url,
      method,
      path,
      body,
      finalHeaders,
      responseBody,
      text,
      responseHeaders,
      sanitizedRoute,
    );
  }

  if (shouldReportEmptyBody(url, response.status, text, responseHeaders)) {
    return emptyResponseBodyFailure({
      path,
      method,
      url,
      headers: responseHeaders,
      status: response.status,
      route: sanitizedRoute,
    });
  }

  return { success: true, data: responseBody as T, text, status: response.status, headers: responseHeaders };
}
